import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { useConfig, whatsappLink } from "@/context/ConfigContext";
import { track } from "@/lib/analytics";

const NAV = [
  { id: "how-it-works", label: "How It Works" },
  { id: "pricing", label: "Pricing" },
  { id: "reviews", label: "Reviews" },
  { id: "faq", label: "FAQ" },
];

export const Header = () => {
  const config = useConfig();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const goOrder = () => {
    track("order_now_click", { location: "header" });
    navigate("/order");
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background/85 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 lg:px-8">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2">
          <span className="font-heading text-xl font-extrabold tracking-tight text-foreground">
            VISA<span className="text-primary">FLIGHT</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {NAV.map((n) => (
            <a
              key={n.id}
              href={`/#${n.id}`}
              data-testid={`nav-${n.id}`}
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={whatsappLink(config)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp"
            onClick={() => track("whatsapp_click", { location: "header" })}
            className="hidden items-center gap-2 rounded-sm border border-border px-3 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary hover:text-primary sm:flex"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </a>
          <Button
            data-testid="header-order-now"
            onClick={goOrder}
            className="hidden rounded-sm bg-primary font-semibold text-primary-foreground hover:bg-[#003DB3] md:inline-flex"
          >
            Order Now
          </Button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" data-testid="mobile-menu-trigger" className="rounded-sm md:hidden">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="mt-8 flex flex-col gap-1">
                {NAV.map((n) => (
                  <SheetClose asChild key={n.id}>
                    <a
                      href={`/#${n.id}`}
                      data-testid={`mobile-nav-${n.id}`}
                      className="rounded-sm px-3 py-3 text-base font-medium text-foreground hover:bg-secondary"
                    >
                      {n.label}
                    </a>
                  </SheetClose>
                ))}
                <Button
                  data-testid="mobile-order-now"
                  onClick={() => {
                    setOpen(false);
                    goOrder();
                  }}
                  className="mt-4 rounded-sm bg-primary font-semibold text-primary-foreground hover:bg-[#003DB3]"
                >
                  Order Now
                </Button>
                <a
                  href={whatsappLink(config)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track("whatsapp_click", { location: "mobile-menu" })}
                  className="mt-2 flex items-center justify-center gap-2 rounded-sm border border-border px-3 py-3 text-base font-medium hover:border-primary hover:text-primary"
                >
                  <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
